import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { TruckIcon, UsersIcon, MessageSquareIcon, CheckCircleIcon, ClockIcon, TrendingUpIcon } from 'lucide-react'
import { Link, router } from '@inertiajs/react'
import { FunnelChart, Funnel, LabelList, ResponsiveContainer, Tooltip, AreaChart, Area, XAxis, YAxis, CartesianGrid, PieChart, Pie, Cell, Legend, BarChart, Bar } from 'recharts'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

interface Stats {
  total: number
  active_pipeline: number
  hired_this_month: number
  avg_time_to_hire: number
  response_rate: number
}

interface FunnelData {
  stage: string
  count: number
  fill: string
}

interface TrendsData {
  date: string
  count: number
}

interface SourceData {
  name: string
  value: number
  fill: string
}

interface DriverTypeData {
  type: string
  count: number
  fill: string
}

interface StatusData {
  status: string
  count: number
  fill: string
}

interface ConversionMetrics {
  application_to_screen: number
  screen_to_background: number
  background_to_offer: number
  offer_to_hire: number
  overall_conversion: number
}

interface MessagesStats {
  total_conversations: number
  unread_conversations: number
}

interface RecruitingDashboardProps {
  stats: Stats
  funnel_data: FunnelData[]
  trends_data: TrendsData[]
  source_data: SourceData[]
  driver_type_data: DriverTypeData[]
  status_data: StatusData[]
  conversion_metrics: ConversionMetrics
  messages_stats: MessagesStats
  current_period: string
}

export default function RecruitingDashboard({
  stats,
  funnel_data,
  trends_data,
  source_data,
  driver_type_data,
  status_data,
  conversion_metrics,
  messages_stats,
  current_period
}: RecruitingDashboardProps) {
  const handlePeriodChange = (period: string) => {
    router.get('/recruiting/dashboard', { period }, { preserveScroll: true })
  }
  return (
    <div className="flex flex-1 flex-col min-w-0">
      <div className="@container/main flex flex-1 flex-col gap-2 min-w-0">
        <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 min-w-0">
          {/* Header */}
          <div className="px-4 lg:px-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold tracking-tight">Modern Nomad Driver Recruiting Dashboard</h1>
                <p className="text-muted-foreground">Comprehensive overview of driver recruitment analytics</p>
              </div>
              <Select value={current_period} onValueChange={handlePeriodChange}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Enhanced Statistics Cards */}
          <div className="px-4 lg:px-6">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Applicants</CardTitle>
                  <UsersIcon className="size-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.total}</div>
                  <p className="text-xs text-muted-foreground">All time applications</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Active Pipeline</CardTitle>
                  <TruckIcon className="size-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.active_pipeline}</div>
                  <p className="text-xs text-muted-foreground">In screening process</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Hired This Month</CardTitle>
                  <CheckCircleIcon className="size-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.hired_this_month}</div>
                  <p className="text-xs text-muted-foreground">Successfully hired</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Avg Time to Hire</CardTitle>
                  <ClockIcon className="size-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.avg_time_to_hire}</div>
                  <p className="text-xs text-muted-foreground">Days to complete</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Response Rate</CardTitle>
                  <TrendingUpIcon className="size-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.response_rate}%</div>
                  <p className="text-xs text-muted-foreground">Moved past initial</p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Row 1: Recruitment Funnel + Application Trends */}
          <div className="px-4 lg:px-6">
            <div className="grid gap-4 lg:grid-cols-3">
              {/* Recruitment Funnel (2/3 width) */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Recruitment Funnel</CardTitle>
                  <CardDescription>Conversion through hiring pipeline stages</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <FunnelChart>
                      <Tooltip
                        content={({ payload }) => {
                          if (payload && payload.length > 0) {
                            const data = payload[0].payload
                            return (
                              <div className="rounded-lg border bg-background p-2 shadow-sm">
                                <div className="grid gap-2">
                                  <div className="flex flex-col">
                                    <span className="text-[0.70rem] uppercase text-muted-foreground">
                                      {data.stage}
                                    </span>
                                    <span className="font-bold text-muted-foreground">
                                      {data.count} applicants
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )
                          }
                          return null
                        }}
                      />
                      <Funnel
                        dataKey="count"
                        data={funnel_data}
                        isAnimationActive
                      >
                        <LabelList position="right" fill="#000" stroke="none" dataKey="stage" />
                      </Funnel>
                    </FunnelChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Application Trends (1/3 width) */}
              <Card>
                <CardHeader>
                  <CardTitle>Application Trends</CardTitle>
                  <CardDescription>Last 30 days</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={trends_data}>
                      <defs>
                        <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis
                        dataKey="date"
                        stroke="#888888"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        stroke="#888888"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <div className="rounded-lg border bg-background p-2 shadow-sm">
                                <div className="grid gap-2">
                                  <div className="flex flex-col">
                                    <span className="text-[0.70rem] uppercase text-muted-foreground">
                                      {payload[0].payload.date}
                                    </span>
                                    <span className="font-bold text-muted-foreground">
                                      {payload[0].value} applications
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )
                          }
                          return null
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="count"
                        stroke="#3b82f6"
                        fillOpacity={1}
                        fill="url(#colorCount)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Row 2: Source Breakdown + Driver Type Distribution */}
          <div className="px-4 lg:px-6">
            <div className="grid gap-4 lg:grid-cols-2">
              {/* Source Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle>Source Breakdown</CardTitle>
                  <CardDescription>Application sources distribution</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={source_data}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {source_data.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip
                        content={({ payload }) => {
                          if (payload && payload.length > 0) {
                            const data = payload[0]
                            return (
                              <div className="rounded-lg border bg-background p-2 shadow-sm">
                                <div className="grid gap-2">
                                  <div className="flex flex-col">
                                    <span className="text-[0.70rem] uppercase text-muted-foreground">
                                      {data.name}
                                    </span>
                                    <span className="font-bold text-muted-foreground">
                                      {data.value} applications
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )
                          }
                          return null
                        }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Driver Type Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle>Driver Type Distribution</CardTitle>
                  <CardDescription>Breakdown by driver category</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={driver_type_data}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis
                        dataKey="type"
                        stroke="#888888"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        stroke="#888888"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip
                        content={({ payload }) => {
                          if (payload && payload.length > 0) {
                            const data = payload[0].payload
                            return (
                              <div className="rounded-lg border bg-background p-2 shadow-sm">
                                <div className="grid gap-2">
                                  <div className="flex flex-col">
                                    <span className="text-[0.70rem] uppercase text-muted-foreground">
                                      {data.type}
                                    </span>
                                    <span className="font-bold text-muted-foreground">
                                      {data.count} drivers
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )
                          }
                          return null
                        }}
                      />
                      <Bar dataKey="count" fill="#3b82f6" radius={[8, 8, 0, 0]}>
                        {driver_type_data.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Row 3: Status Distribution */}
          <div className="px-4 lg:px-6">
            <Card>
              <CardHeader>
                <CardTitle>Status Distribution</CardTitle>
                <CardDescription>Current applicant statuses across the pipeline</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={status_data}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="status"
                      stroke="#888888"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      stroke="#888888"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      content={({ payload }) => {
                        if (payload && payload.length > 0) {
                          const data = payload[0].payload
                          return (
                            <div className="rounded-lg border bg-background p-2 shadow-sm">
                              <div className="grid gap-2">
                                <div className="flex flex-col">
                                  <span className="text-[0.70rem] uppercase text-muted-foreground">
                                    {data.status}
                                  </span>
                                  <span className="font-bold text-muted-foreground">
                                    {data.count} applicants
                                  </span>
                                </div>
                              </div>
                            </div>
                          )
                        }
                        return null
                      }}
                    />
                    <Bar dataKey="count" fill="#3b82f6" radius={[8, 8, 0, 0]}>
                      {status_data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Row 4: Conversion Metrics */}
          <div className="px-4 lg:px-6">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">App → Screen</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{conversion_metrics.application_to_screen}%</div>
                  <p className="text-xs text-muted-foreground">Application to phone screen</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Screen → BG Check</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{conversion_metrics.screen_to_background}%</div>
                  <p className="text-xs text-muted-foreground">Phone screen to background</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">BG Check → Offer</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{conversion_metrics.background_to_offer}%</div>
                  <p className="text-xs text-muted-foreground">Background to offer</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Offer → Hired</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{conversion_metrics.offer_to_hire}%</div>
                  <p className="text-xs text-muted-foreground">Offer to hired</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Overall Conversion</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{conversion_metrics.overall_conversion}%</div>
                  <p className="text-xs text-muted-foreground">Application to hired</p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Quick Links Section */}
          <div className="px-4 lg:px-6">
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
                <CardDescription>Manage your recruitment activities</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 md:grid-cols-3">
                  <Link
                    href="/driver_leads"
                    className="flex items-center gap-3 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <UsersIcon className="size-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">View All Applicants</p>
                      <p className="text-sm text-muted-foreground">Manage driver applications</p>
                    </div>
                  </Link>

                  <Link
                    href="/recruiting/messages"
                    className="flex items-center gap-3 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <MessageSquareIcon className="size-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Messages</p>
                      <p className="text-sm text-muted-foreground">
                        {messages_stats.unread_conversations} unread
                      </p>
                    </div>
                  </Link>

                  <Link
                    href="/driver_leads/new"
                    className="flex items-center gap-3 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <TruckIcon className="size-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Add Driver Lead</p>
                      <p className="text-sm text-muted-foreground">Create new application</p>
                    </div>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
